import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  MapPin, Clock, User, Phone, Play, Home, AlertCircle, CheckCircle2,
  Navigation, ChevronLeft, ChevronRight, Calendar as CalendarIcon, FileText,
} from 'lucide-react';
import { toast } from 'sonner';
import AideVisitView from '../../components/aide/AideVisitView';
import MyWeekStrip from '../../components/dashboard/MyWeekStrip';
import InServiceTrainingCard from '../../components/inservice/InServiceTrainingCard';
import KudosWidget from '../../components/kudos/KudosWidget';
import { Link } from 'react-router-dom';
import { ShieldAlert } from 'lucide-react';

function formatTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  } catch { return iso; }
}

function toYmd(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function todayYmd() { return toYmd(new Date()); }

function friendlyDate(ymd) {
  // "Today" / "Yesterday" / "Tomorrow" / "Mon, Apr 23"
  const d = new Date(`${ymd}T12:00:00`);
  const today = new Date(); today.setHours(12, 0, 0, 0);
  const diffDays = Math.round((d - today) / 86400000);
  if (diffDays === 0) return 'Today';
  if (diffDays === -1) return 'Yesterday';
  if (diffDays === 1) return 'Tomorrow';
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

function statusTone(s) {
  if (s === 'completed') return { bg: '#DCFCE7', text: '#166534', label: 'Completed' };
  if (s === 'in_progress') return { bg: '#FEF3C7', text: '#92400E', label: 'In Progress' };
  if (s === 'missed') return { bg: '#FEE2E2', text: '#991B1B', label: 'Missed' };
  if (s === 'cancelled') return { bg: '#F1F5F9', text: '#475569', label: 'Cancelled' };
  return { bg: '#DBEAFE', text: '#1E40AF', label: 'Scheduled' };
}

export default function AideDashboard() {
  const { user } = useAuth();
  const [selectedDate, setSelectedDate] = useState(todayYmd());
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeVisit, setActiveVisit] = useState(null);
  const [clockingId, setClockingId] = useState(null);

  const isToday = selectedDate === todayYmd();
  const isPast = selectedDate < todayYmd();
  const isFuture = selectedDate > todayYmd();

  const load = async (dateYmd = selectedDate) => {
    setLoading(true);
    try {
      const res = await api.get('/aide/my-visits', { params: { date: dateYmd } });
      setVisits(res.data || []);
    } catch (e) {
      console.error('Load visits failed:', e);
      toast.error('Could not load your visits');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(selectedDate); }, [selectedDate]);

  const shiftDate = (deltaDays) => {
    const d = new Date(`${selectedDate}T12:00:00`);
    d.setDate(d.getDate() + deltaDays);
    setSelectedDate(toYmd(d));
  };

  const getLocation = () => new Promise((resolve) => {
    if (!navigator.geolocation) return resolve(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({
        lat: pos.coords.latitude,
        lng: pos.coords.longitude,
        accuracy: pos.coords.accuracy,
      }),
      () => resolve(null),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  });

  const handleClockIn = async (visit) => {
    setClockingId(visit.id);
    try {
      const location = await getLocation();
      if (!location) toast.warning('GPS unavailable — visit will be flagged for review');
      const res = await api.post(`/care-logs/assignments/${visit.id}/clock-in`, {
        location,
        device_info: navigator.userAgent,
      });
      toast.success('Clocked in successfully');
      setActiveVisit(res.data);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Clock-in failed');
    } finally {
      setClockingId(null);
    }
  };

  const onVisitComplete = async () => {
    setActiveVisit(null);
    await load();
  };

  const groups = useMemo(() => ({
    inProgress: visits.filter(v => v.status === 'in_progress'),
    scheduled: visits.filter(v => v.status === 'scheduled'),
    completed: visits.filter(v => v.status === 'completed'),
    missed: visits.filter(v => v.status === 'missed'),
    cancelled: visits.filter(v => v.status === 'cancelled'),
  }), [visits]);

  if (activeVisit) {
    return <AideVisitView visit={activeVisit} onDone={onVisitComplete} onCancel={() => setActiveVisit(null)} />;
  }

  // Title for the "upcoming/scheduled" list — wording changes per date.
  const scheduledTitle = isPast ? 'Scheduled (not clocked in)' : isFuture ? 'Scheduled Visits' : 'Upcoming Visits';

  return (
    <div className="max-w-3xl mx-auto space-y-4 pb-20" data-testid="aide-dashboard">
      {/* Greeting + Date Picker */}
      <div className="px-1">
        <h1 className="text-2xl sm:text-3xl font-bold text-[#0A2342]">Hi, {user?.name?.split(' ')[0] || 'Aide'}</h1>
        <p className="text-sm text-[#64748B]">{friendlyDate(selectedDate)} · {new Date(`${selectedDate}T12:00:00`).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}</p>
      </div>

      {/* Quick action — submit a patient incident report */}
      <Link to="/incidents/new" data-testid="aide-incident-cta" className="block">
        <Card className="careiq-card border-l-4 border-l-[#DC2626] hover:shadow-md transition-shadow">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-[#FEE2E2] flex items-center justify-center flex-shrink-0">
              <ShieldAlert className="w-6 h-6 text-[#DC2626]" />
            </div>
            <div className="flex-1">
              <div className="font-semibold text-[#0A2342]">Report a patient incident</div>
              <div className="text-xs text-[#64748B]">Fall, cut, medication error, behavioral change… your team will be notified instantly.</div>
            </div>
            <ChevronRight className="w-5 h-5 text-[#94A3B8]" />
          </CardContent>
        </Card>
      </Link>

      {/* My Week strip — 7-day overview with clickable day tiles */}
      <MyWeekStrip role="aide" selectedDate={selectedDate} onSelectDate={setSelectedDate} />

      {/* Date filter controls */}
      <Card className="careiq-card" data-testid="aide-date-filter">
        <CardContent className="p-3 flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            className="h-10 w-10 p-0 shrink-0"
            onClick={() => shiftDate(-1)}
            data-testid="aide-date-prev"
            aria-label="Previous day"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <div className="flex-1 relative">
            <CalendarIcon className="w-4 h-4 text-[#64748B] absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <Input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value || todayYmd())}
              className="h-10 pl-9 text-base"
              data-testid="aide-date-input"
            />
          </div>
          <Button
            variant="outline"
            size="sm"
            className="h-10 w-10 p-0 shrink-0"
            onClick={() => shiftDate(1)}
            data-testid="aide-date-next"
            aria-label="Next day"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
          {!isToday && (
            <Button
              variant="ghost"
              size="sm"
              className="h-10 text-xs shrink-0"
              onClick={() => setSelectedDate(todayYmd())}
              data-testid="aide-date-today"
            >Today</Button>
          )}
        </CardContent>
      </Card>

      {/* Stats cards — labels adapt to date */}
      <div className="grid grid-cols-3 gap-2 sm:gap-3">
        <Card className="careiq-card"><CardContent className="p-3 text-center">
          <div className="text-2xl font-bold text-[#0E7490]" data-testid="aide-stat-total">{visits.length}</div>
          <div className="text-[11px] text-[#64748B] uppercase tracking-wide">Visits</div>
        </CardContent></Card>
        <Card className="careiq-card"><CardContent className="p-3 text-center">
          <div className="text-2xl font-bold text-[#92400E]" data-testid="aide-stat-active">{groups.inProgress.length}</div>
          <div className="text-[11px] text-[#64748B] uppercase tracking-wide">{isToday ? 'Active' : 'Open'}</div>
        </CardContent></Card>
        <Card className="careiq-card"><CardContent className="p-3 text-center">
          <div className="text-2xl font-bold text-[#166534]" data-testid="aide-stat-done">{groups.completed.length}</div>
          <div className="text-[11px] text-[#64748B] uppercase tracking-wide">Done</div>
        </CardContent></Card>
      </div>

      {/* In-Progress — only relevant today */}
      {isToday && groups.inProgress.length > 0 && (
        <Card className="careiq-card border-l-4 border-[#F59E0B]">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Play className="w-4 h-4 text-[#F59E0B]" />Visit In Progress
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {groups.inProgress.map(v => (
              <div key={v.id} className="border border-[#FEF3C7] bg-[#FFFBEB] rounded-xl p-3 space-y-2">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-semibold text-[#0A2342]">{v.patient_name}</div>
                    <div className="text-xs text-[#64748B]">Started {formatTime(v.clock_in_at)}</div>
                  </div>
                  <Badge style={{ backgroundColor: '#FEF3C7', color: '#92400E' }}>In Progress</Badge>
                </div>
                <Button
                  onClick={() => setActiveVisit(v)}
                  className="w-full bg-[#F59E0B] hover:bg-[#D97706] text-white h-12 text-base"
                  data-testid={`resume-visit-${v.id}`}
                >Resume Visit →</Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Scheduled list */}
      <Card className="careiq-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Home className="w-4 h-4 text-[#0E7490]" />{scheduledTitle}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {loading ? (
            <div className="text-center py-8 text-[#64748B]">Loading…</div>
          ) : groups.scheduled.length === 0 ? (
            <div className="text-center py-6 text-[#64748B] text-sm" data-testid="no-upcoming-visits">
              {isPast ? 'No unstarted visits on this day.' : isFuture ? 'Nothing on the schedule yet.' : (
                <><CheckCircle2 className="w-10 h-10 mx-auto mb-2 text-[#22C55E]" />No upcoming visits right now</>
              )}
            </div>
          ) : groups.scheduled.map(v => {
            const tone = statusTone(v.status);
            return (
              <div key={v.id} className="border border-[#E2E8F0] rounded-xl p-3 space-y-2" data-testid={`visit-card-${v.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-[#0A2342] flex items-center gap-1.5">
                      <User className="w-3.5 h-3.5 text-[#64748B]" />{v.patient_name}
                    </div>
                    <div className="text-xs text-[#64748B] mt-1 flex items-center gap-1.5">
                      <Clock className="w-3 h-3" />
                      {formatTime(v.scheduled_start)} – {formatTime(v.scheduled_end)}
                    </div>
                    {v.patient_address && (
                      <div className="text-xs text-[#64748B] mt-1 flex items-center gap-1.5">
                        <MapPin className="w-3 h-3" /><span className="truncate">{v.patient_address}</span>
                      </div>
                    )}
                    {v.patient_phone && (
                      <a href={`tel:${v.patient_phone}`} className="text-xs text-[#0E7490] mt-1 flex items-center gap-1.5 underline">
                        <Phone className="w-3 h-3" />{v.patient_phone}
                      </a>
                    )}
                    {v.notes && (
                      <div className="text-xs italic text-[#475569] mt-1.5">Note: {v.notes}</div>
                    )}
                  </div>
                  <Badge style={{ backgroundColor: tone.bg, color: tone.text }}>{tone.label}</Badge>
                </div>

                {/* Clock-in only valid for today */}
                {isToday && (
                  <Button
                    onClick={() => handleClockIn(v)}
                    disabled={clockingId === v.id}
                    className="w-full bg-[#0E7490] hover:bg-[#0C6580] text-white h-12 text-base"
                    data-testid={`clock-in-btn-${v.id}`}
                  >
                    {clockingId === v.id ? (<>Getting GPS…</>) : (<><Navigation className="w-4 h-4 mr-2" />Clock In with GPS</>)}
                  </Button>
                )}
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Completed — shown for any date with completions */}
      {groups.completed.length > 0 && (
        <Card className="careiq-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2 text-[#166534]">
              <CheckCircle2 className="w-4 h-4" />Completed {isToday ? 'Today' : ''}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {groups.completed.map(v => (
              <div key={v.id} className="border border-[#DCFCE7] bg-[#F0FDF4] rounded-lg p-3 space-y-1.5" data-testid={`completed-visit-${v.id}`}>
                <div className="flex justify-between items-start gap-2">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-[#0A2342] truncate">{v.patient_name}</div>
                    <div className="text-[11px] text-[#166534] flex items-center gap-1.5 mt-0.5">
                      <Clock className="w-3 h-3" />
                      {formatTime(v.clock_in_at)} – {formatTime(v.clock_out_at)}
                      {v.clock_in_at && v.clock_out_at && (
                        <span className="text-[#64748B]">
                          · {Math.max(1, Math.round((new Date(v.clock_out_at) - new Date(v.clock_in_at)) / 60000))} min
                        </span>
                      )}
                    </div>
                    {v.clock_in_location && (
                      <div className="text-[10px] text-[#64748B] flex items-center gap-1 mt-0.5">
                        <MapPin className="w-2.5 h-2.5" />GPS verified
                      </div>
                    )}
                    {(v.care_log || v.notes) && (
                      <div className="text-[11px] text-[#475569] mt-1 flex items-start gap-1">
                        <FileText className="w-3 h-3 mt-0.5 shrink-0" />
                        <span className="line-clamp-2">{(v.care_log?.notes) || v.notes}</span>
                      </div>
                    )}
                  </div>
                  <Badge style={{ backgroundColor: '#DCFCE7', color: '#166534' }} className="shrink-0">Done</Badge>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Missed / Cancelled collapsed summary — only for past dates */}
      {(isPast || !isToday) && (groups.missed.length > 0 || groups.cancelled.length > 0) && (
        <Card className="careiq-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base text-[#991B1B] flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />Missed / Cancelled
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {[...groups.missed, ...groups.cancelled].map(v => {
              const tone = statusTone(v.status);
              return (
                <div key={v.id} className="flex justify-between items-center p-2 rounded bg-[#FEF2F2] text-sm" data-testid={`missed-visit-${v.id}`}>
                  <div>
                    <div className="font-medium text-[#0A2342]">{v.patient_name}</div>
                    <div className="text-[11px] text-[#64748B]">{formatTime(v.scheduled_start)} – {formatTime(v.scheduled_end)}</div>
                  </div>
                  <Badge style={{ backgroundColor: tone.bg, color: tone.text }}>{tone.label}</Badge>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {!loading && visits.length === 0 && (
        <Card className="careiq-card">
          <CardContent className="text-center py-12" data-testid="aide-no-visits">
            <AlertCircle className="w-10 h-10 mx-auto mb-3 text-[#64748B]" />
            <h3 className="font-semibold text-[#0A2342]">
              {isToday ? 'No visits assigned today' : isFuture ? 'Nothing scheduled for this day' : 'No visits on this day'}
            </h3>
            <p className="text-sm text-[#64748B] mt-1">
              {isToday ? 'Check back later or contact your supervisor.' : 'Pick a different date above to browse your schedule or history.'}
            </p>
          </CardContent>
        </Card>
      )}

      {/* In-service training — aide reads own log + marks complete */}
      <InServiceTrainingCard userId={user?.id} variant="aide" />

      {/* Peer recognition / Employee of the Week */}
      <KudosWidget />
    </div>
  );
}
